import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useMutation, useQuery } from '@apollo/client/react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import BaseButton from 'src/components/Buttons/BaseButton'
import { useAuthProvider } from 'src/context/AuthProvider'
import useLogger from 'src/context/LoggerProvider/useLogger'
import {
  DirectoryClaimCodeChannel,
  DirectoryClaimStatus,
  IDirectoryClaimSelf,
  IMutation,
  IMutationAttachDirectoryClaimDocumentArgs,
  IMutationLinkDirectoryClaimArgs,
  IMutationSendDirectoryClaimCodeArgs,
  IMutationStartDirectoryClaimArgs,
  IMutationStartDirectoryClaimGuestArgs,
  IMutationSubmitDirectoryClaimArgs,
  IMutationVerifyDirectoryClaimCodeArgs,
  IQuery,
  IQueryDirectoryClaimDemandArgs,
  IQueryMyDirectoryClaimArgs
} from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import { numberWithCommas } from 'src/utils'
import { DirectoryCategory } from 'src/utils/directory'
import {
  trackDirectoryClaimDemandShown,
  trackDirectoryClaimLinked,
  trackDirectoryClaimStart,
  trackDirectoryClaimSubmitted
} from 'src/utils/pixels/directoryEvents'
import {
  safeStorageGet,
  safeStorageRemove,
  safeStorageSet
} from 'src/utils/safeStorage'
import ClaimEvidence from './ClaimEvidence'
import {
  ATTACH_DIRECTORY_CLAIM_DOCUMENT,
  DIRECTORY_CLAIM_DEMAND,
  LINK_DIRECTORY_CLAIM,
  MY_DIRECTORY_CLAIM,
  SEND_DIRECTORY_CLAIM_CODE,
  START_DIRECTORY_CLAIM,
  START_DIRECTORY_CLAIM_GUEST,
  SUBMIT_DIRECTORY_CLAIM,
  VERIFY_DIRECTORY_CLAIM_CODE
} from './claimQuery'
import ClaimStartForm, { ClaimStartValues } from './ClaimStartForm'
import { FOCUS_RING } from './claimTheme'

/** Stable across SSR/CSR — `aria-controls` needs a deterministic id. */
const PANEL_ID = 'directory-claim-panel'

interface ClaimFacilityProps {
  locationId: string
  fichaPath: string
  category: DirectoryCategory
}

/**
 * Below this the market line stays hidden — HostCTA's same bar. The figure is
 * already amplified server-side (3x, rounded up to 10), so a market too quiet
 * to clear 10 genuinely has nothing to pitch with.
 */
const NEARBY_MIN_FOR_NUMERAL = 10

/**
 * Contacts an UNCLAIMED ficha hands out per month — mirrors
 * `DIRECTORY_REVEAL_CAP_PER_MONTH` in
 * `spotme-server/src/api/directory/revealLedger.ts`, tied by this comment the
 * same way `DIRECTORY_VIEWPORT_SIZE` is tied to the resolver's page size.
 *
 * COPY ONLY. It exists to tell an owner why `revealsThisMonth` is a small
 * number — the figure is a rationed sample, not his demand. Drift misstates
 * the cap in one sentence of the pitch; it can never change what the server
 * hands out, which is enforced in the ledger.
 */
const UNCLAIMED_CONTACT_CAP_PER_MONTH = 3

/**
 * How many more visits a MONTH a published listing gets than a directory
 * ficha — the "why claim" number, and the one figure in this panel that is a
 * public claim about our own performance, so it is stated as an AVERAGE and
 * nothing else.
 *
 * MEASURED 2026-09-03, same instrument on both sides (PostHog, MX-only, 30d:
 * `view_item` per published listing vs `$pageview` per `/directorio/` path):
 *   · published  276 viewed of 294 live — 16.4 views / 12.5 sessions avg
 *   · fichas     538 viewed of 3,727 live — 2.4 views / 1.3 sessions avg
 *   ⇒ 6.9x on views, 9.4x on sessions, 9x at p90 → the 5-10x band.
 *
 * Three things this number is NOT, all of which the copy respects:
 *   · not a MEDIAN claim — median-to-median is 2.5x (5 vs 2), so the copy
 *     says "en promedio" and must never say "típicamente" or "hasta".
 *   · not causal — our listings concentrate in MTY/CDMX/GDL while fichas span
 *     67 metros, so part of the gap is market mix. Never phrase it as
 *     "reclamar multiplica tus visitas por N".
 *   · not about REQUESTS. Measured the same day: published listings average
 *     0.9 leads/month with 70% at ZERO (max 21), and the reveal ledger shows
 *     ~1 reveal at the facilities that get any. There is no defensible
 *     requests multiple — do not extend this band to solicitudes/leads.
 *
 * RE-MEASURE before widening the band or reusing it on a bigger surface:
 * `yarn set-listing-levers --dump` (spotme-server) + query 8 in
 * `sales/directory/weekly-readout.md`.
 */
const CLAIM_VISIT_MULTIPLE_MIN = 5
const CLAIM_VISIT_MULTIPLE_MAX = 10

/**
 * The claim panel on a directory ficha (plan §3.1).
 *
 * Progressive disclosure rather than a modal: the claim belongs to the facility
 * owner, not the renter reading the page, so it stays collapsed until asked
 * for — and an inline panel needs no focus trap, no scroll lock and no portal
 * on a page that also renders inside a mobile drawer elsewhere in the app.
 *
 * The evidence ladder requires an account (team invite, wizard, signed-URL
 * document upload, the same identity posture as manual KYC). Since 2026-08-28
 * the START does not: a guest leaves name + WhatsApp first and the draft is
 * adopted by the account they sign in with (`linkDirectoryClaim`, token in
 * session storage) — 5 of the first 5 claim-starts had died on the login link
 * (spec docs/plans/2026-08-28-002). The REVEAL on the same page stays public.
 */
const ClaimFacility = ({
  locationId,
  fichaPath,
  category
}: ClaimFacilityProps) => {
  const { t } = useTranslation('directorio')
  const logger = useLogger()
  const { currentUser, isAuthenticated } = useAuthProvider()
  const [open, setOpen] = useState(false)
  /**
   * Pointer/focus intent on the CTA arms the claim-status query BEFORE the
   * click, so the panel usually opens onto the form instead of a "Cargando…"
   * line — the round trip rides the ~300-800ms between hover and click.
   * Touch devices get no hover; they still fetch on open, as before.
   */
  const [armed, setArmed] = useState(false)
  const [claim, setClaim] = useState<IDirectoryClaimSelf | null>(null)
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [startFailed, setStartFailed] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  // Only fetch once the panel is open AND the caller is signed in: an
  // authenticated query on every ficha render would 401 for the (overwhelming)
  // majority of visitors, who are renters.
  const { data, loading: loadingClaim } = useQuery<
    Pick<IQuery, 'myDirectoryClaim'>,
    IQueryMyDirectoryClaimArgs
  >(MY_DIRECTORY_CLAIM, {
    variables: { locationId },
    skip: (!open && !armed) || !isAuthenticated,
    // `cache-first`, not `network-only`: the hover prefetch is only useful if
    // the open then READS what it fetched. Freshness is not load-bearing here
    // — every mutation in this panel replaces `claim` from its own response,
    // so the only staleness window is an admin decision landing mid-session,
    // which the next page load picks up.
    fetchPolicy: 'cache-first'
  })

  useEffect(() => {
    if (data?.myDirectoryClaim) setClaim(data.myDirectoryClaim)
  }, [data])

  // Demand evidence for the pitch (§4.3, on-page half). PUBLIC — unlike the
  // claim-status query it also serves the login wall, where the numbers do
  // their best work. Same hover-arming as the claim query; on error the block
  // simply doesn't render (also the graceful path while a fresh client build
  // fronts a not-yet-deployed server schema).
  const { data: demandData } = useQuery<
    Pick<IQuery, 'directoryClaimDemand'>,
    IQueryDirectoryClaimDemandArgs
  >(DIRECTORY_CLAIM_DEMAND, {
    variables: { locationId },
    skip: !open && !armed,
    fetchPolicy: 'cache-first'
  })
  const demand = demandData?.directoryClaimDemand
  // The >= 1 here is DELIBERATE and asymmetric with NEARBY_MIN_FOR_NUMERAL's
  // 10 (questioned and reaffirmed by Jules 2026-08-31): the market figure is a
  // STATISTIC — weak values embarrass, so it hides below 10 — while a reveal
  // is an EVENT ("a real renter asked for YOUR contact this month"), personal
  // even at 1, and shown only after the owner opts in by opening the panel.
  // Do not raise this to "fix" thin pitch ammo — the don't-lead-with-1 rule
  // applies to the OUTREACH motion (sales/directory/README.md), not this panel.
  const showReveals = (demand?.revealsThisMonth ?? 0) >= 1
  const showCapped = (demand?.cappedAttemptsThisMonth ?? 0) >= 1
  const showNearby = (demand?.nearbySearches ?? 0) >= NEARBY_MIN_FOR_NUMERAL
  // Once a claim is decided (or awaiting review) the pitch has done its job.
  const isPreDecision = !claim || claim.status === DirectoryClaimStatus.Draft
  const showDemand =
    isPreDecision && (showReveals || showCapped || showNearby) && !!demand

  useEffect(() => {
    if (!open || !showDemand) return
    trackDirectoryClaimDemandShown({
      locationId,
      nearbyShown: showNearby,
      revealsShown: showReveals,
      cappedShown: showCapped
    })
  }, [open, showDemand, showNearby, showReveals, showCapped, locationId])

  // The panel replaces the CTA the operator activated, so without moving focus
  // the caret lands back on <body> and a screen reader announces nothing.
  useEffect(() => {
    if (open) panelRef.current?.focus()
  }, [open])

  const [startClaim, { loading: starting }] = useMutation<
    Pick<IMutation, 'startDirectoryClaim'>,
    IMutationStartDirectoryClaimArgs
  >(START_DIRECTORY_CLAIM)
  const [startClaimGuest, { loading: startingGuest }] = useMutation<
    Pick<IMutation, 'startDirectoryClaimGuest'>,
    IMutationStartDirectoryClaimGuestArgs
  >(START_DIRECTORY_CLAIM_GUEST)
  const [linkClaim] = useMutation<
    Pick<IMutation, 'linkDirectoryClaim'>,
    IMutationLinkDirectoryClaimArgs
  >(LINK_DIRECTORY_CLAIM)
  // A guest draft's capability — session-scoped: it is the only way back to
  // the draft before the account exists, and it dies with the tab on purpose.
  const guestTokenKey = `directory:claim:guest:${locationId}`
  const [guestToken, setGuestToken] = useState<string | null>(() =>
    safeStorageGet('session', guestTokenKey)
  )
  const linkedRef = useRef(false)
  // The wall is crossed: the account that just signed in adopts the draft.
  useEffect(() => {
    if (!isAuthenticated || !guestToken || linkedRef.current) return
    linkedRef.current = true
    linkClaim({ variables: { guestToken } })
      .then(({ data: result }) => {
        if (result?.linkDirectoryClaim) {
          setClaim(result.linkDirectoryClaim)
          trackDirectoryClaimLinked(locationId)
        }
      })
      .catch((err) => {
        logger.warn('Directory guest claim link failed', { err, locationId })
      })
      .finally(() => {
        safeStorageRemove('session', guestTokenKey)
        setGuestToken(null)
      })
  }, [
    isAuthenticated,
    guestToken,
    guestTokenKey,
    linkClaim,
    locationId,
    logger
  ])
  const [sendCode, { loading: sending }] = useMutation<
    Pick<IMutation, 'sendDirectoryClaimCode'>,
    IMutationSendDirectoryClaimCodeArgs
  >(SEND_DIRECTORY_CLAIM_CODE)
  const [verifyCode, { loading: verifying }] = useMutation<
    Pick<IMutation, 'verifyDirectoryClaimCode'>,
    IMutationVerifyDirectoryClaimCodeArgs
  >(VERIFY_DIRECTORY_CLAIM_CODE)
  const [attachDocument, { loading: attaching }] = useMutation<
    Pick<IMutation, 'attachDirectoryClaimDocument'>,
    IMutationAttachDirectoryClaimDocumentArgs
  >(ATTACH_DIRECTORY_CLAIM_DOCUMENT)
  const [submitClaim, { loading: submitting }] = useMutation<
    Pick<IMutation, 'submitDirectoryClaim'>,
    IMutationSubmitDirectoryClaimArgs
  >(SUBMIT_DIRECTORY_CLAIM)

  /**
   * ONLY a GraphQL error's own message reaches the user. The server's claim
   * errors are deliberately actionable ("we have no mobile on record", "wait a
   * moment before requesting another code") — but an Apollo transport failure
   * would otherwise render the literal string "Failed to fetch" inside a
   * Spanish alert, which is both useless and a leak of internals.
   */
  const serverMessage = useCallback(
    (err: unknown): string => {
      if (CombinedGraphQLErrors.is(err) && err.errors[0]?.message) {
        return err.errors[0].message
      }
      return t('CLAIM.GENERIC_ERROR')
    },
    [t]
  )

  const busy = sending || verifying || attaching || submitting

  const handleStart = useCallback(
    async (values: ClaimStartValues) => {
      setStartFailed(false)
      try {
        if (!isAuthenticated) {
          // Guest start (2026-08-28): name + WhatsApp first, account later.
          const { data: result } = await startClaimGuest({
            variables: { input: { locationId, ...values } }
          })
          const started = result?.startDirectoryClaimGuest
          if (!started) {
            setStartFailed(true)
            return
          }
          safeStorageSet('session', guestTokenKey, started.guestToken)
          setGuestToken(started.guestToken)
          setClaim(started.claim)
          return
        }
        const { data: result } = await startClaim({
          variables: { input: { locationId, ...values } }
        })
        if (!result?.startDirectoryClaim) {
          setStartFailed(true)
          return
        }
        setClaim(result.startDirectoryClaim)
      } catch (err) {
        logger.warn('Directory claim start failed', { err, locationId })
        setStartFailed(true)
      }
    },
    [
      isAuthenticated,
      startClaim,
      startClaimGuest,
      guestTokenKey,
      locationId,
      logger
    ]
  )

  const handleSendCode = useCallback(
    async (channel: DirectoryClaimCodeChannel) => {
      if (!claim) return
      setError(null)
      try {
        const { data: result } = await sendCode({
          variables: { claimId: claim.id, channel }
        })
        setCodeSentTo(result?.sendDirectoryClaimCode.sentTo ?? null)
      } catch (err) {
        // The server's message here IS actionable ("no mobile on record",
        // "wait a moment") — unlike the reveal, where a uniform error is a
        // deliberate anti-oracle. Surface it.
        setError(serverMessage(err))
      }
    },
    [claim, sendCode, serverMessage]
  )

  const handleVerify = useCallback(
    async (code: string) => {
      if (!claim) return
      setError(null)
      try {
        const { data: result } = await verifyCode({
          variables: { claimId: claim.id, code }
        })
        if (result?.verifyDirectoryClaimCode) {
          setClaim(result.verifyDirectoryClaimCode)
          setCodeSentTo(null)
        }
      } catch (err) {
        setError(serverMessage(err))
      }
    },
    [claim, verifyCode, serverMessage]
  )

  const handleAttach = useCallback(
    async (document: { id: string; name: string; url: string }) => {
      if (!claim) return
      setError(null)
      try {
        const { data: result } = await attachDocument({
          variables: {
            claimId: claim.id,
            // `nonPublic: true` is not decoration — the server REFUSES a
            // public-bucket key, because that bucket is world-readable forever.
            document: { ...document, nonPublic: true }
          }
        })
        if (result?.attachDirectoryClaimDocument) {
          setClaim(result.attachDirectoryClaimDocument)
        }
      } catch (err) {
        setError(serverMessage(err))
      }
    },
    [claim, attachDocument, serverMessage]
  )

  const handleSubmit = useCallback(async () => {
    if (!claim) return
    setError(null)
    try {
      const { data: result } = await submitClaim({
        variables: { claimId: claim.id }
      })
      if (result?.submitDirectoryClaim) {
        setClaim(result.submitDirectoryClaim)
        trackDirectoryClaimSubmitted({
          locationId,
          strength: result.submitDirectoryClaim.strength
        })
      }
    } catch (err) {
      setError(serverMessage(err))
    }
  }, [claim, submitClaim, locationId, serverMessage])

  const openPanel = useCallback(() => {
    setOpen(true)
    trackDirectoryClaimStart({
      locationId,
      surface: 'ficha',
      mode: isAuthenticated ? 'auth' : 'guest'
    })
  }, [locationId, isAuthenticated])

  const body = (() => {
    if (!isAuthenticated) {
      // The wall moved (2026-08-28): a guest fills the form FIRST; the account
      // is asked for at the evidence rung, once the draft exists. Before the
      // flip 5 of 5 claim-starts died on the login link right here.
      if (claim) {
        return (
          <>
            <p className="text-[14px] leading-relaxed font-medium text-ink-700">
              {t('CLAIM.GUEST_SAVED_TITLE')}
            </p>
            <p className="mt-1 text-[14px] leading-relaxed text-ink-200">
              {t('CLAIM.GUEST_SAVED_BODY')}
            </p>
            <Link
              href={`${RedirectUrls.Login}?redirectL=${encodeURIComponent(fichaPath)}`}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-800 px-6 py-3 text-sm font-medium text-white-500 transition-transform duration-200 hover:translate-x-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700"
            >
              {t('CLAIM.GUEST_SAVED_CTA')}
            </Link>
          </>
        )
      }
      return (
        <ClaimStartForm
          loading={startingGuest}
          failed={startFailed}
          requirePhone
          defaults={{ contactName: '', contactPhone: '' }}
          onSubmit={handleStart}
        />
      )
    }
    if (loadingClaim && !claim) {
      return <p className="text-[14px] text-ink-200">{t('CLAIM.LOADING')}</p>
    }
    if (!claim) {
      return (
        <ClaimStartForm
          loading={starting}
          failed={startFailed}
          defaults={{
            contactName: currentUser?.firstname
              ? `${currentUser.firstname} ${currentUser.lastname ?? ''}`.trim()
              : '',
            contactPhone: currentUser?.phoneNumber ?? ''
          }}
          onSubmit={handleStart}
        />
      )
    }
    if (claim.status === DirectoryClaimStatus.Submitted) {
      return (
        <p className="text-[14px] leading-relaxed text-ink-200">
          {t('CLAIM.STATUS_SUBMITTED')}
        </p>
      )
    }
    if (claim.status === DirectoryClaimStatus.Rejected) {
      return (
        <>
          <p className="text-[14px] leading-relaxed text-ink-200">
            {t('CLAIM.STATUS_REJECTED')}
          </p>
          {claim.rejectionReason && (
            <p className="mt-2 text-[14px] leading-relaxed text-ink-700">
              {claim.rejectionReason}
            </p>
          )}
        </>
      )
    }
    if (claim.status === DirectoryClaimStatus.Approved) {
      return (
        <p className="text-[14px] leading-relaxed text-ink-200">
          {t('CLAIM.STATUS_APPROVED')}
        </p>
      )
    }
    return (
      <ClaimEvidence
        claim={claim}
        busy={busy}
        codeSentTo={codeSentTo}
        error={error}
        onSendCode={handleSendCode}
        onVerifyCode={handleVerify}
        onAttachDocument={handleAttach}
        onSubmit={handleSubmit}
      />
    )
  })()

  return (
    <>
      {/* A real disclosure: the trigger STAYS mounted and carries
          `aria-expanded`/`aria-controls`, so the state is exposed and the panel
          can be collapsed again. The earlier version destroyed the button on
          open, which made the state unrepresentable and left a screen-reader
          user with no signal that anything had happened. */}
      <BaseButton
        variant="outlined"
        className={`w-full rounded-full md:w-auto ${FOCUS_RING}`}
        onClick={open ? () => setOpen(false) : openPanel}
        onPointerEnter={() => setArmed(true)}
        onFocus={() => setArmed(true)}
        aria-expanded={open}
        aria-controls={PANEL_ID}
      >
        {open ? t('CLAIM.CLOSE_CTA') : t('FICHA.CLAIM_CTA')}
      </BaseButton>
      {open && (
        // `role="region"` — `aria-label` is prohibited on the implicit
        // `generic` role of a bare <div> and is simply ignored, so the focus
        // target would have had no accessible name at all.
        <div
          id={PANEL_ID}
          ref={panelRef}
          role="region"
          tabIndex={-1}
          aria-label={t('CLAIM.A11Y_REGION')}
          // `md:basis-full` — in the ficha's wrapped flex row (the wrapper is
          // `md:contents`) this puts the open panel on its own full-width
          // line instead of the CTA's narrow column.
          className="mt-5 w-full rounded-2xl border border-ink-100/15 bg-cream-300 p-5 outline-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2 md:basis-full md:p-6"
        >
          <h3 className="font-display text-[19px] leading-tight font-medium text-ink-700 md:text-xl">
            {t('CLAIM.PANEL_TITLE')}
          </h3>
          {showDemand && demand && (
            <div className="mt-3 rounded-xl bg-white-500 p-4">
              {/* The MARKET figure leads, and the facility's own lines sit
                  under it as what reached this ficha out of that. Reversed
                  2026-09-03 (Jules): with the reveal line on top, "1 persona
                  pidió tu contacto" read as the whole demand for the
                  facility, when it is a sample rationed by
                  UNCLAIMED_CONTACT_CAP_PER_MONTH — a number too small to
                  pitch with, and an actively bad look on the one surface
                  meant to make claiming attractive. */}
              {showNearby && (
                <p className="font-display text-[17px] leading-snug font-medium text-ink-700 md:text-[19px]">
                  {t('CLAIM.DEMAND_MARKET', {
                    count: `${numberWithCommas(demand.nearbySearches)}${
                      demand.nearbySearchesTier === 3 ? '+' : ''
                    }`,
                    category:
                      category === DirectoryCategory.Storage
                        ? t('CLAIM.DEMAND_CATEGORY.bodegas')
                        : t('CLAIM.DEMAND_CATEGORY.estacionamientos')
                  })}
                </p>
              )}
              {(showReveals || showCapped) && (
                <ul
                  className={`flex flex-col gap-1.5 ${showNearby ? 'mt-2' : ''}`}
                >
                  {showReveals && (
                    <li className="text-[14px] leading-relaxed font-medium text-ink-700">
                      {t('CLAIM.DEMAND_REVEALS', {
                        count: demand.revealsThisMonth
                      })}
                    </li>
                  )}
                  {showCapped && (
                    <li className="text-[14px] leading-relaxed font-medium text-ink-700">
                      {t('CLAIM.DEMAND_CAPPED', {
                        count: demand.cappedAttemptsThisMonth
                      })}
                    </li>
                  )}
                </ul>
              )}
              {/* Why the facility line is small, stated in OUR terms before
                  the owner concludes it is HIS demand: an unclaimed ficha is
                  a muted, unbookable card below the ranked results whose
                  contact we hand out a fixed few times a month. Every clause
                  is a fact of the code (DirectoryCard: no photo, no
                  availability, "Ver ficha" not "Reservar"; revealLedger's
                  cap).
                  The cap is stated as a CEILING ("un máximo de"), never as
                  the reason this ficha's number is small: the ledger has 0
                  capped attempts across 54 facility-months, so "your
                  requests were throttled" would be false today. */}
              <p className="mt-3 text-[13px] leading-relaxed text-ink-200">
                {t('CLAIM.DEMAND_LIMIT', {
                  cap: UNCLAIMED_CONTACT_CAP_PER_MONTH
                })}
              </p>
              <p className="mt-1.5 text-[13px] leading-relaxed font-medium text-ink-700">
                {t('CLAIM.DEMAND_MULTIPLE', {
                  min: CLAIM_VISIT_MULTIPLE_MIN,
                  max: CLAIM_VISIT_MULTIPLE_MAX
                })}
              </p>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ink-700">
                {t('CLAIM.DEMAND_PITCH')}
              </p>
            </div>
          )}
          <div className="mt-2">{body}</div>
        </div>
      )}
    </>
  )
}

export default React.memo(ClaimFacility)
